import {
  Box,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormLabel,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InvoiceProfileService } from "subModule/src/core/services";
import AlertMessages from "common/Alert/AlertMessages";
import { useSnackbar } from "notistack";
import SimpleModal from "common/Form/Form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { checkPrivileges } from "Helpers/CheckPrivileges";
import { useEffect, useState, useRef } from "react";
import Loader from "layout/Loader";
import AccountSettingsTabs from "./AccountSettingsTabs";
import DescriptionIcon from "@mui/icons-material/Description";
import { ReactComponent as Accounts } from "../../assets/SideBarIcons/accounts.svg";
import {
  CustomSettings,
  BusinessTemplateType,
  designVariantToColumn,
  designVariantToKey,
  stripDesignVariantFromAttributes,
  withDesignVariantInSettings,
} from "subModule/src/services/invoice/types";
import {
  GetInvoiceTemplateByIdResponse,
  InvoiceTemplate,
} from "subModule/src/services/invoice/interface";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { BrandingResponse } from "subModule/src/services/allcommon/interface";
import { themes } from "theme/themes";
import EditIcon from "@mui/icons-material/Edit";
import FormDialog from "common/Modal/Modal";
import InvoiceTemplateCustomizer from "components/InvoiceTemplate/InvoiceTemplateCustomizer";
import ComponentSubheader from "common/ComponentHeaderTwo/ComponentSubheader";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
interface FormData {
  [key: string]: any;
}
export default function InvoiceTemplatesModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();

  const { enqueueSnackbar } = useSnackbar();
  const [openPreview, setOpenPreview] = useState<boolean>(true);
  const [templateSettings, setTemplateSettings] = useState<
    CustomSettings | undefined
  >();
  const {
    register,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    reset,
    setValue,
    trigger,
    getValues,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      type: 2, // Default to INVOICE
      designVariant: 2, // MODERN
    },
  });

  const templateType = watch("type") ?? 2;
  const isDefaultTemplate = Boolean(watch("default") || watch("isDefault"));
  const branding = useSelector(brandingInfo);
  const themeColors =
    (branding as BrandingResponse)?.attributes?.themeId === 100
      ? (branding as BrandingResponse)?.attributes?.customTheme
      : (themes as any)[
          (branding as BrandingResponse)?.attributes?.themeId || 3
        ];
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(true);
  const getCurrentSettingsRef = useRef<(() => CustomSettings) | null>(null);
  const applySettingsChangeRef = useRef<
    ((newSettings: Partial<CustomSettings>) => void) | null
  >(null);
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "businessTemplates",
    moduleHref: "/business-templates",
    entityFallbackLabelKey: "name",
    isEdit: !!params?.id,
  });
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    InvoiceProfileService.getInvoiceTemplateByID(+(params as any)?.id)
      .then((res: GetInvoiceTemplateByIdResponse) => {
        if (res.success) {
          const templateData = res?.data || {};
          const settings = withDesignVariantInSettings(
            (templateData.settings ||
              templateData.attributes) as CustomSettings,
            templateData.designVariant,
          );
          reset({
            ...templateData,
            type: templateData.type ?? 2,
            designVariant: designVariantToColumn(
              templateData.designVariant ?? settings.designVariant,
            ),
            settings,
            attributes: settings,
          });
          setTemplateSettings(settings);
          captureLabel(templateData?.name);
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      });
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);

    // Get the latest settings from customizer if available, otherwise use templateSettings
    let latestSettings = templateSettings || data.settings;
    if (getCurrentSettingsRef.current) {
      try {
        const currentSettings = getCurrentSettingsRef.current();
        if (currentSettings) {
          latestSettings = currentSettings;
        }
      } catch (error) {
        console.warn("Could not get current settings from customizer:", error);
      }
    }

    const designVariant =
      Number(data.type ?? 2) === BusinessTemplateType.INVOICE
        ? designVariantToColumn(
            latestSettings?.designVariant ?? data.designVariant,
          )
        : designVariantToColumn("classic");
    // designVariant is stored as its own column — strip from attributes JSON
    const attributes = stripDesignVariantFromAttributes(
      latestSettings || data.settings,
    );

    const submitData: InvoiceTemplate = {
      name: data.name || "",
      description: data.description || "",
      type: data.type ?? 2,
      designVariant,
      attributes,
    };

    if (params?.id) {
      InvoiceProfileService.updateInvoiceTemplate({
        ...submitData,
        id: data?.id,
        default: data?.default,
        active: data?.active,
      })
        .then((response: any) => {
          if (response.success) {
            // navigate(-1);
            captureLabel(data?.name);
            getByID();
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("template") + " " + t("updated") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        })
        .finally(() => setIsBtnEnabled(true));
    } else {
      InvoiceProfileService.createInvoiceTemplate(submitData)
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("template") + " " + t("created") + " " + t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            { variant: "error" },
          );
        })
        .finally(() => setIsBtnEnabled(true));
    }
  };

  const handleSettingsSave = async (settings: CustomSettings) => {
    setTemplateSettings(settings);
    setValue("settings", settings);
    setValue("attributes", settings);
    setValue("designVariant", designVariantToColumn(settings.designVariant));
    setIsBtnEnabled(true);
    const isValid = await trigger();
    if (isValid) onSubmit(getValues());
  };

  const handleSettingsChange = (settings: CustomSettings) => {
    setTemplateSettings(settings);
    setValue("settings", settings);
    setValue("attributes", settings);
    setValue("designVariant", designVariantToColumn(settings.designVariant));
  };
  return (
    <>
      <ComponentHeaderTwo
        showBtn={false}
        showSaveBtn={false}
        onClick={() => navigate("/business-template/create")}
        heading={t("accountSettings")}
        icon={<Accounts height={28} width={28} />}
        btnText={"create"}
        btnType={"create"}
        breadcrumbItems={breadcrumbItems}
      />
      <div className={`flex`}>
        <AccountSettingsTabs selectedIndex={2} />
        <Box className="w-[100%] mt-[10px]">
          <ComponentSubheader
            showBtn={false}
            showSaveBtn={true}
            onClick={() => navigate(-1)}
            onSubmit={handleSubmit(onSubmit)}
            heading={
              params?.id ? t("editBusinessTemplate") : t("addBusinessTemplate")
            }
            icon={
              <DescriptionIcon
                sx={{
                  fill: "#000",
                  width: "30px",
                  height: "30px",
                  marginTop: "3px",
                }}
              />
            }
            btnText={"back"}
            btnType={"back"}
            isBtnEnabled={IsBtnEnabled}
            showCreateBtn={checkPrivileges("ROLE_INVOICE_TEMPLATE_CREATE")}
          />
          {loading ? (
            <Loader />
          ) : (
            <SimpleModal
              visable={true}
              title={
                params?.id
                  ? t("editBusinessTemplate")
                  : t("addBusinessTemplate233")
              }
              isButtonVisable={true}
              isVisableBtn={IsBtnEnabled}
              btnclosetext="Back"
              buttonText={params?.id ? "update" : "submit"}
              attBtnNotshow={false}
              notVisableBackbtn={true}
              showPortList={handleSubmit(onSubmit)}
              formSubmit={handleSubmit(onSubmit)}
              content={
                <Grid
                  container
                  component="form"
                  id="form"
                  className="mb-2"
                  rowSpacing={3}
                  columnSpacing={2}
                  onSubmit={handleSubmit(onSubmit)}
                >
                  {/* <Grid item xs={12} md={3}>
                    <TextField
                      {...register("name", {
                        validate: {
                          required: (value) =>
                            value.trim() !== "" || t("This field is required."),
                        },
                      })}
                      label={t("name") + " *"}
                      placeholder={t("name")}
                      title={t("name")}
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message as string}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid> */}
                  {/* <Grid item xs={12} md={6}>
                    <TextField
                      {...register("description")}
                      label={t("description")}
                      placeholder={t("description")}
                      title={t("description")}
                      fullWidth
                      error={!!errors.description}
                      helperText={errors.description?.message as string}
                      InputLabelProps={{ shrink: true }}
                      multiline
                      rows={1}
                      sx={{
                        "& .MuiInputBase-fullWidth.MuiInputBase-multiline": {
                          width: "100%",
                          paddingRight: "0px",
                          paddingBottom: 0,
                        },
                        "& .MuiInputBase-inputMultiline": {
                          resize: "vertical",
                          minHeight: "29px",
                        },
                      }}
                    />
                  </Grid> */}
                  <Grid item xs={12} md={3}></Grid>
                  <Grid item xs={12}>
                    <Button
                      className="secondary flex gap-1"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => setOpenPreview(true)}
                    >
                      <EditIcon className="w-[20px] h-[20px]" />{" "}
                      {t("customizeTemplate")}
                    </Button>
                  </Grid>
                  {openPreview && (
                    <FormDialog
                      open={openPreview}
                      onClose={(event, reason) =>
                        event && reason !== "backdropClick" ? navigate(-1) : ""
                      }
                      title={t("customizeTemplate")}
                      hideActions
                      fullScreen
                      hideTitle
                      content={
                        <Box sx={{ minHeight: "600px" }}>
                          <InvoiceTemplateCustomizer
                            templateId={
                              params?.id ? +(params as any)?.id : undefined
                            }
                            isViewMode={location.state?.isViewMode || false}
                            initialSettings={templateSettings}
                            onSave={handleSettingsSave}
                            onCancel={() => navigate(-1)}
                            params={params}
                            IsBtnEnabled={IsBtnEnabled}
                            onSettingsChange={handleSettingsChange}
                            getCurrentSettings={(getter) => {
                              getCurrentSettingsRef.current = getter;
                            }}
                            getApplySettingsChange={(applier) => {
                              applySettingsChangeRef.current = applier;
                            }}
                            templateType={templateType}
                            isDefault={isDefaultTemplate}
                            formFields={
                              <Grid container id="form" spacing={2}>
                                <Grid item xs={12}>
                                  <TextField
                                    {...register("name", {
                                      validate: {
                                        required: (value) =>
                                          value.trim() !== "" ||
                                          t("This field is required."),
                                      },
                                    })}
                                    label={t("name") + " *"}
                                    placeholder={t("phTemplateName")}
                                    title={t("name")}
                                    fullWidth
                                    error={!!errors.name}
                                    helperText={errors.name?.message as string}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                </Grid>
                                <Grid item xs={12}>
                                  <FormControl fullWidth>
                                    <InputLabel shrink>
                                      {t("type") + " *"}
                                    </InputLabel>
                                    <Select
                                      {...register("type", {
                                        validate: {
                                          required: (value) =>
                                            (value !== undefined &&
                                              value !== null) ||
                                            t("This field is required."),
                                        },
                                      })}
                                      size="small"
                                      value={getValues("type") ?? 2}
                                      onChange={(e) => {
                                        const nextType = Number(e.target.value);
                                        setValue("type", nextType);
                                        if (
                                          nextType !==
                                          BusinessTemplateType.INVOICE
                                        ) {
                                          setValue("designVariant", 1);
                                          applySettingsChangeRef.current?.({
                                            designVariant: "classic",
                                          });
                                        } else {
                                          setValue("designVariant", 2);
                                          const current =
                                            getCurrentSettingsRef.current?.() ||
                                            templateSettings ||
                                            {};
                                          applySettingsChangeRef.current?.({
                                            designVariant: "modern",
                                            vehicleFields: {
                                              ...current.vehicleFields,
                                              plateNumber: true,
                                              mileage:
                                                current.vehicleFields
                                                  ?.mileage !== false,
                                            },
                                            companyFields: {
                                              ...current.companyFields,
                                              vat: true,
                                            },
                                          });
                                        }
                                        trigger("type");
                                      }}
                                      error={!!errors.type}
                                      label={t("type") + " *"}
                                    >
                                      <MenuItem value={2}>INVOICE</MenuItem>
                                      <MenuItem value={3}>JOBCARD</MenuItem>
                                      <MenuItem value={4}>WORKORDER</MenuItem>
                                      <MenuItem value={5}>INSPECTION</MenuItem>
                                      <MenuItem value={6}>
                                        DELIVERY NOTE
                                      </MenuItem>
                                      <MenuItem value={7}>
                                        PROFORMA INVOICE
                                      </MenuItem>
                                    </Select>
                                    {errors.type && (
                                      <Box
                                        sx={{
                                          color: "error.main",
                                          fontSize: "0.75rem",
                                          mt: 0.5,
                                          ml: 1.75,
                                        }}
                                      >
                                        {errors.type.message as string}
                                      </Box>
                                    )}
                                  </FormControl>
                                </Grid>
                                {Number(templateType) ===
                                BusinessTemplateType.INVOICE ? (
                                  <Grid item xs={12}>
                                    <FormControl fullWidth>
                                      <FormLabel
                                        sx={{
                                          mb: 1,
                                          fontSize: "0.75rem",
                                          color: "text.secondary",
                                        }}
                                      >
                                        {t("designVariant")}
                                      </FormLabel>
                                      <ToggleButtonGroup
                                        exclusive
                                        size="small"
                                        value={designVariantToKey(
                                          watch("designVariant") ??
                                            templateSettings?.designVariant,
                                        )}
                                        onChange={(_e, value) => {
                                          if (!value) return;
                                          const current =
                                            getCurrentSettingsRef.current?.() ||
                                            templateSettings ||
                                            {};
                                          const next: Partial<CustomSettings> =
                                            {
                                              designVariant: value as
                                                | "classic"
                                                | "modern",
                                            };
                                          if (value === "modern") {
                                            next.vehicleFields = {
                                              ...current.vehicleFields,
                                              plateNumber: true,
                                              mileage:
                                                current.vehicleFields
                                                  ?.mileage !== false,
                                            };
                                            next.companyFields = {
                                              ...current.companyFields,
                                              vat: true,
                                            };
                                          }
                                          setValue(
                                            "designVariant",
                                            designVariantToColumn(value),
                                          );
                                          applySettingsChangeRef.current?.(
                                            next,
                                          );
                                        }}
                                        sx={{
                                          backgroundColor: "#F4F6F8",
                                          borderRadius: "10px",
                                          p: "3px",
                                          "& .MuiToggleButton-root": {
                                            border: "none",
                                            borderRadius: "8px !important",
                                            textTransform: "none",
                                            px: 2,
                                            py: 0.5,
                                            fontFamily: "Saira, sans-serif",
                                            fontWeight: 600,
                                            fontSize: 12,
                                            color: "#6B7A8D",
                                            "&.Mui-selected": {
                                              backgroundColor: "#fff",
                                              color:
                                                themeColors?.primary ||
                                                "#2D2C5D",
                                              boxShadow:
                                                "0 1px 3px rgba(0,0,0,0.08)",
                                            },
                                          },
                                        }}
                                      >
                                        {" "}
                                        <ToggleButton value="modern">
                                          {t("modernDesign")}
                                        </ToggleButton>
                                        <ToggleButton value="classic">
                                          {t("classicDesign")}
                                        </ToggleButton>
                                      </ToggleButtonGroup>
                                    </FormControl>
                                  </Grid>
                                ) : null}
                                <Grid item xs={12}>
                                  <TextField
                                    {...register("description")}
                                    label={t("description")}
                                    placeholder={t("phTemplateDescription")}
                                    title={t("description")}
                                    fullWidth
                                    error={!!errors.description}
                                    helperText={
                                      errors.description?.message as string
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    multiline
                                    rows={1}
                                    sx={{
                                      "& .MuiInputBase-fullWidth.MuiInputBase-multiline":
                                        {
                                          width: "100%",
                                          paddingRight: "0px",
                                          paddingBottom: 0,
                                        },
                                      "& .MuiInputBase-inputMultiline": {
                                        resize: "vertical",
                                        minHeight: "29px",
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                            }
                            formData={watch()}
                          />
                        </Box>
                      }
                    />
                  )}
                </Grid>
              }
            />
          )}
        </Box>
      </div>
    </>
  );
}
